import { FetchBaseQueryMeta } from "@reduxjs/toolkit/dist/query";
import { z } from "zod";
import api, {
  getBackstageURL,
  PaginationResponse,
  Option,
  OptionEntity,
} from "services/api";

import regexCustomURL from "utils/regexCustomURL";

export const chartSchema = z.object({
  boardDesc: z.string(),
  boardName: z.string().min(1, "必填欄位").max(500, "最長 500 個字"),
  boardType: z.string(),
  dataSource: z.string(),
  htmlText: z.string(),
  htmlTextMobile: z.string(),
  id: z.number(),
  isHot: z.boolean(),
  isSysHot: z.boolean().optional(),
  mhid: z.number(),
  sort: z.number(),
  thumbnail: z.instanceof(File).optional(),
  thumbnailFileName: z.string().optional(),
  unitName: z.string().max(500, "最長 500 個字"),
  url: z
    .string()
    .max(500, "最長 500 個字")
    .regex(regexCustomURL, "只允許輸入英文與數字")
    .optional(),
  display: z.number(),
  publicDate: z.string().optional(),
  updateDate: z.string().optional(),
  updateUser: z.string().optional(),
  enableDate: z.string().optional(),
  viewsCount: z.number().optional(),
  isHotFrom: z.date().optional().nullable(),
  isHotTo: z.date().optional().nullable(),
  createDate: z.string().optional(),
});

export type Chart = z.infer<typeof chartSchema>;

const extendedApi = api.injectEndpoints({
  endpoints: (build) => ({
    charts: build.query<
      PaginationResponse<Chart>,
      {
        page?: number;
        perPage?: number;
        categoryId?: string;
        isHot?: string;
        display?: string;
        search?: string;
        sort?: string;
      }
    >({
      query: ({
        page = 1,
        perPage = 30,
        categoryId,
        isHot,
        display,
        search,
        sort,
      }) => ({
        url: getBackstageURL("/charts"),
        params: {
          page,
          perPage,
          categoryId: categoryId ?? undefined,
          isHot: isHot ?? undefined,
          display: display ?? undefined,
          search,
          sort,
        },
      }),
      providesTags: (result, error, arg) =>
        result
          ? [
              ...result.items.map(({ id }) => ({
                type: "Chart" as const,
                id,
              })),
              "Chart",
            ]
          : ["Chart"],
      transformResponse: (res: Chart[], meta: FetchBaseQueryMeta) => {
        const headerTotalCount = +meta.response?.headers.get("total-count");

        return {
          totalCount: Number.isNaN(headerTotalCount) ? 0 : headerTotalCount,
          items: res ?? [],
        };
      },
    }),
    chartViewsCountEntity: build.query<OptionEntity, void>({
      query: () => getBackstageURL("/charts/views-count"),
      transformResponse: (res: { id: number; viewsCount: number }[]) => {
        const entity: OptionEntity = { ids: [], entities: {} };

        res.forEach((item) => {
          entity.ids.push(`${item.id}`);
          entity.entities[`${item.id}`] = {
            label: `${item.id}`,
            value: `${item.viewsCount}`,
          };
        });

        return entity;
      },
    }),
    chart: build.query<Chart, string>({
      query: (id) => getBackstageURL(`/charts/${id}`),
      providesTags: (result, error, arg) =>
        result ? [{ type: "Chart", id: arg }] : [],
    }),
    chartEntity: build.query<OptionEntity, void>({
      query: () => getBackstageURL("/charts/options"),
      transformResponse: (data: Option[] = []) => {
        const entity: OptionEntity = {
          ids: [],
          entities: {},
        };

        data.forEach((x) => {
          entity.ids.push(x.value);
          entity.entities[x.value] = x;
        });

        return entity;
      },
    }),
    createChart: build.mutation<Chart, Chart>({
      query: (params) => {
        const formData = new FormData();

        for (let key in params) {
          if ((key === "isHotFrom" || key === "isHotTo") && params[key]) {
            formData.append(key, params[key].toISOString());
            continue;
          }

          formData.append(key, params[key] ?? "");
        }

        return {
          url: getBackstageURL("/charts"),
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: ["Chart"],
    }),
    updateChart: build.mutation<Chart, Chart>({
      query: (params) => {
        const formData = new FormData();

        for (let key in params) {
          if ((key === "isHotFrom" || key === "isHotTo") && params[key]) {
            formData.append(key, params[key].toISOString());
            continue;
          }

          formData.append(key, params[key] ?? "");
        }

        return {
          url: getBackstageURL(`/charts/${params.id}`),
          method: "PUT",
          body: formData,
        };
      },
      invalidatesTags: (result, error, arg) => [{ type: "Chart", id: arg.id }],
    }),
    deleteChart: build.mutation<void, number>({
      query: (id) => ({
        url: getBackstageURL(`/charts/${id}`),
        method: "DELETE",
      }),
      invalidatesTags: ["Chart"],
    }),
  }),
  overrideExisting: false,
});

export const {
  useChartsQuery,
  useChartViewsCountEntityQuery,
  useChartQuery,
  useChartEntityQuery,
  useCreateChartMutation,
  useDeleteChartMutation,
  useUpdateChartMutation,
} = extendedApi;
